import { Consts } from './Consts';

/**
 * Position in Geodetic Coordinates.
 * Ported from Java by Mark Movshovich
*/
export class GeoPosition
{

	/**
	 * lat - measured lat
	 * lon - measured lon
	 * height - measured height
	 */
  constructor(private lat: number, private lon: number, private height: number) {
	}

  public getLat(): number {
    return this.lat;
  }

  public getLon(): number {
    return this.lon;
  }

  public getHeight(): number {
    return this.height;
  }

	// Methods

	/**
	 * lat - Latitude in Geodetic CS
	 * height - in m
	 * return Latitude in Geographic CS
	 */
	public static geodeticToReduced(lat: number, height: number): number {
		let sinLat = Math.sin(lat);
		let coslat = Math.cos(lat);
		let t = Math.hypot(Consts.MAJOR_AXIS * coslat, Consts.MINOR_AXIS * sinLat);
		let up = (Consts.MAJOR_AXIS + height) * (Consts.MINOR_AXIS * Consts.MINOR_AXIS + t * height);
		let down = (Consts.MINOR_AXIS + height) * (Consts.MAJOR_AXIS * Consts.MAJOR_AXIS + t * height);
		return Math.atan((up / down) * (sinLat / coslat));
	}

	/**
	 * reducedLat - latitude in Geographic CS
	 * height - meters
	 * return Latitude in Geodetic CS
	 */
	public static reducedToGeodetic(reducedLat: number, height: number): number {
		let lat = reducedLat;
		let curr = this.geodeticToReduced(lat, height);
		let err = 1;
		const MAX_NUM_OF_ITERATIONS = 100;
		const  MIN_ERROR = 1e-12;
		for (let i = 0; err > MIN_ERROR && i < MAX_NUM_OF_ITERATIONS; i++) {
			lat = reducedLat - curr + lat;
			curr = this.geodeticToReduced(lat, height);
			err = Math.abs(reducedLat - curr);
		}
		return lat;
	}

 	/**
	 * x - as in (x,y,z)
	 * y - as in (x,y,z)
	 * z - as in (x,y,z)
	 * return The Euclidian Distance of (x,y,z) from (0,0,0).
	 */
	private static hypot3D(x: number, y: number, z: number): number {
		return Math.hypot(Math.hypot(x, y), z);
	}

	/**
	 * polarOrigin - the origin of polar coordinates in geo
	 * return this position in polar coordinates
	 */
	public toPolar(polarOrigin: GeoPosition): PolarPosition
	{
		let originLatReduced = GeoPosition.geodeticToReduced(polarOrigin.getLat(), polarOrigin.getHeight());
		let sinOriginLatReduced = Math.sin(originLatReduced);
		let cosOriginLatReduced = Math.cos(originLatReduced);
		let reducedLat = GeoPosition.geodeticToReduced(this.getLat(), this.getHeight());
		let originHeight = polarOrigin.getHeight();

		// Plot eccentricity normalized according to the point height
		let rh = Consts.MAJOR_AXIS + this.getHeight();
		let e = (Consts.MAJOR_AXIS - Consts.MINOR_AXIS) / rh;

		let r0 = Consts.MAJOR_AXIS + originHeight;
		let e0 = (Consts.MAJOR_AXIS - Consts.MINOR_AXIS) / r0;
		let e01 = 1 - e0;

		let cosReducedLat = Math.cos(reducedLat);
		let sinReducedLat = Math.sin(reducedLat);
		let cosLon = Math.cos(this.getLon());
		let sinLon = Math.sin(this.getLon());

		// Define the plot coordinates in the local coordinate system with
		// origin into
		// the point of sensor replasing
		let xp = rh * cosReducedLat * cosLon;
		let yp = rh * cosReducedLat * sinLon;
		let zp = rh * (1 - e) * sinReducedLat;

		// Convert plot coordinates from the local coordinate system into sensor
		// coordinate system
		let cosOriginLon = Math.cos(polarOrigin.getLon());
		let sinOriginLon = Math.sin(polarOrigin.getLon());
		let x0 = r0 * cosOriginLatReduced * cosOriginLon;
		let y0 = r0 * cosOriginLatReduced * sinOriginLon;
		let z0 = r0 * (1 - e0) * sinOriginLatReduced;
		let deltaX = xp - x0;
		let deltaY = yp - y0;
		let deltaZ = zp - z0;

		// range
		let range = GeoPosition.hypot3D(deltaX, deltaY, deltaZ);
		const rangeThreshold = 0.00001;
		if (range < rangeThreshold) {
			return new PolarPosition(range, 0, this.getHeight(), polarOrigin);
		}

		// azimuth
		let b = e01 * cosOriginLatReduced;
		let t = e01 * e01 * cosOriginLatReduced * cosOriginLatReduced;
		let sq = Math.sqrt(t + sinOriginLatReduced * sinOriginLatReduced);
		let g = -sq * (deltaX * sinOriginLon - deltaY * cosOriginLon);
		let h = (deltaX * cosOriginLon + deltaY * sinOriginLon) * sinOriginLatReduced - deltaZ * b;

		const hThrehold = 0.000002;
		if (Math.abs(h) < hThrehold) {
			return new PolarPosition(range, 0, this.getHeight(), polarOrigin);
		}

		let azimuth = Math.atan2(h, g) + Math.PI / 2;
		if (azimuth >= 2 * Math.PI) {
			azimuth -= 2 * Math.PI;
		}
		if (azimuth < 0) {
			azimuth += 2 * Math.PI;
		}

		return new PolarPosition(range, azimuth, this.getHeight(), polarOrigin);
	}

	/**
	 * origin - of the spherical coordinates
	 * return the Position in Spherical coordinates (SphericalPosition)
	 */
	public toSpherical(origin: GeoPosition): SphericalPosition {
		return this.toPolar(origin).toSpherical();
	}

}

/**
 * Position in Polar Coordinates.
 * Ported from Java by Mark Movshovich
 */
export class PolarPosition
{
	/**
	 * range - measured range
	 * azimuth - measured azimuth
	 * height - measured height
	 * origin - origin in geodetic (GeoPosition)
	 */
	constructor(private range: number, private azimuth: number, private height: number, private origin: GeoPosition) {
	}

  public getRange(): number {
    return this.range;
  }

  public getAzimuth(): number {
    return this.azimuth;
  }

  public getHeight(): number {
    return this.height;
  }

  public getOrigin(): GeoPosition {
    return this.origin;
  }

	/**
	 * return The position in Spherical coordinates
	 */
	public toSpherical(): SphericalPosition {
		let rPoint = Consts.EARTH_RADIUS + this.getHeight();
		let rOrigin = Consts.EARTH_RADIUS + this.getOrigin().getHeight();
		let rRatio = rPoint / rOrigin;
		let range = this.getRange() / rOrigin;
		const t = 0.5 * ((rRatio * rRatio - 1) / range - range);

		let elevation;
		const eps = 0.000002;
		if (t > 1 - eps) {
			elevation = Math.PI / 2;
		} else {
			if (t < -1 - eps) {
				elevation = -Math.PI / 2;
			} else {
				elevation = Math.asin(t);
			}
		}
		return new SphericalPosition(this.getRange(), this.getAzimuth(), elevation, this.getOrigin());
	}

	/**
	 * return The Polar Position in Geodetic Coordinates.
	 */
	public toGeo(): GeoPosition
	{
		let sinOriginLon = Math.sin(this.getOrigin().getLon());
		let cosOriginLon = Math.cos(this.getOrigin().getLon());
		let originHeight = this.getOrigin().getHeight();
		let plotHeight = this.getHeight();
		let plotAzimuth = this.getAzimuth();
		let plotRange = this.getRange();

		// Transforme input (Geodetic) latitude to internal reduced
		// latitude
		let originLatReduced = GeoPosition.geodeticToReduced(this.getOrigin().getLat(), originHeight);
		let sinOriginLatReduced = Math.sin(originLatReduced);
		let cosOriginLatReduced = Math.cos(originLatReduced);

		// Plot eccentricity normalized according to the point height
		let deltaAxis = Consts.MAJOR_AXIS - Consts.MINOR_AXIS;
		let rh = Consts.MAJOR_AXIS + plotHeight;
		let eps1 = deltaAxis / rh;

		// Set initial value for the plot latitude
		let sinPlotLat = sinOriginLatReduced;

		// Compute previous distance from Earth center to the plot
		let rp2 = rh * rh * (1 - eps1 * (2 - eps1) * sinPlotLat * sinPlotLat);
		let rp = Math.sqrt(rp2);
		let r0 = Consts.MAJOR_AXIS + originHeight;
		let e1 = 1 - deltaAxis / r0;
		let e12 = e1 * e1;
		let e121 = e12 - 1;
		let rr2 = r0 * r0 * (1 + e121 * sinOriginLatReduced * sinOriginLatReduced);
		let rr = Math.sqrt(rr2);
		let sq = Math.sqrt(e12 * cosOriginLatReduced * cosOriginLatReduced + sinOriginLatReduced
				* sinOriginLatReduced);

		// Compute previous angle of the plot elevation
		let cosAzimuth = Math.cos(plotAzimuth);
		let angle = 4 * plotRange * cosAzimuth / (rr + rp);
		let angle2 = angle * angle;

		// Compute the plot elevation
		let prt1 = angle * sinOriginLatReduced * cosOriginLatReduced * (1 - angle2 * 0.166666667
				* (1 - angle2 / 20));
    let prt2 = 0.25 * angle2 * (2 * cosOriginLatReduced * cosOriginLatReduced - 1) * (1
				- angle2 * 0.083333333 * (1 - angle2 / 30));
    let corr = prt1 + prt2;
    let qq = sinOriginLatReduced * sinOriginLatReduced + corr;

    // Start of iteration process
		let cosTeta = 0;
		let y = 0;
		let z = 0;
		for (let i = 0; i < 2; i++) {
			// Correct distance from Earth center to the plot
			rp2 = rh * rh * (1 - eps1 * (2 - eps1) * qq);

			// Patch when range = 0
			let coefC;
			const MIN_RANGE = 0.01;
			if (plotRange > MIN_RANGE) {
				coefC = sq * (rp2 - rr * rr - plotRange * plotRange) / (2 * r0 * plotRange);
			} else {
				coefC = 0;
			}

			let coefB = e121 * sinOriginLatReduced * cosOriginLatReduced * cosAzimuth;
			let d = coefC + coefB;

			// Patch for case when detection elevation is around 90 deg. Not
			// proved
			let discriminant = e12 - (coefC - coefB) * d;
			if (discriminant < 0) {
				discriminant = 0;
			}

			// Define roots of the equation
			let root1 = (e1 + Math.sqrt(discriminant)) / d;
			let root2 = (e1 - Math.sqrt(discriminant)) / d;

			let cosTeta1 = (1 - root1 * root1) / (1 + root1 * root1);

			// Choose the correct root
			let sinTeta;
			if (cosTeta1 < 0.) {
				cosTeta = (1 - root2 * root2) / (1 + root2 * root2);
				sinTeta = 2 * root2 / (1 + root2 * root2);
			} else {
				cosTeta = cosTeta1;
				sinTeta = 2 * root1 / (1 + root1 * root1);
			}

			// Define the plot coordinates in the local coordinate system with
			// origin into the point of radar replasing
			y = plotRange * cosTeta * cosAzimuth;
			z = plotRange * sinTeta;
			let geoZ = (z * sinOriginLatReduced + e1 * plotRange * cosTeta * cosAzimuth
					* cosOriginLatReduced) / sq;
			geoZ += e1 * r0 * sinOriginLatReduced;

			sinPlotLat = geoZ / rh / (1 - eps1);

			qq = sinPlotLat * sinPlotLat;
		}

		// Define the plot coordinates in the local coordinate system
		let x = plotRange * cosTeta * Math.sin(plotAzimuth);

		// Transformation to the geographical coordinate system
		let d = (e1 * z * cosOriginLatReduced - y * sinOriginLatReduced) / sq;

		let geoX = d * cosOriginLon - x * sinOriginLon;
		let geoY = d * sinOriginLon + x * cosOriginLon;

		d = r0 * cosOriginLatReduced;

		geoX += d * cosOriginLon;
		geoY += d * sinOriginLon;

		// 31.03.05 Transform internal Latitude (Reduced) to output (Geodetic)
		// latitude
		let lat = GeoPosition.reducedToGeodetic(Math.asin(sinPlotLat), plotHeight);
		let lon = Math.atan2(geoY, geoX);
		return new GeoPosition(lat, lon, plotHeight);
	}

}

/**
 * Position in Spherical Coordinates.
 * Ported from Java by Mark Movshovich
 */
export class SphericalPosition 
{
	/**
	 * range - measured range
	 * azimuth - measured azimuth
	 * elevation - measured elevation
	 * origin - geodetic origin
	 */
	constructor(private range: number, private azimuth: number, private elevation: number, private origin: GeoPosition) {
	}

  public getRange(): number {
    return this.range;
  }

  public getAzimuth(): number {
    return this.azimuth;
  }

  public getElevation(): number {
    return this.elevation;
  }

  public getOrigin(): GeoPosition {
    return this.origin;
  }

	/**
	 * return The Position in Polar coordinates.
	 */
	public toPolar(): PolarPosition {
		const epsilon = 0.00001;
		if (this.getRange() < epsilon) {
			return new PolarPosition(this.getRange(), this.getAzimuth(), this.getOrigin().getHeight(),
      this.getOrigin());
		}

		let totalHeight = Consts.EARTH_RADIUS + this.getOrigin().getHeight();
		let r = this.getRange() / totalHeight;
		let t = Math.sqrt(1 + r * r + 2 * r * Math.sin(this.getElevation()));
		let height = ((t * totalHeight) - Consts.EARTH_RADIUS);

		return new PolarPosition(this.getRange(), this.getAzimuth(), height, this.getOrigin());
	}

	/**
	 * return the position in geodetic coordinates.
	 */
	public toGeo(): GeoPosition {
		return this.toPolar().toGeo();
	}

}
